import { useEffect, useState } from 'react';
import { useAuth } from '@services/auth';
import {
	getMyCompanyContexts,
	setMyActiveCompany,
	getStoredCompanyScopeMode,
	setStoredCompanyScopeMode,
	notifyCompanyWorkspaceChanged
} from '@services/companies';

const STRIP_TITLE_MULTI =
	'Lists and calendar follow the workspace you pick here. Use “All companies” to see everything together.';

/**
 * Inline workspace switcher for pages where company context matters (projects, schedule, tasks).
 */
export default function CompanyWorkspaceStrip() {
	const { user, refreshUser } = useAuth();
	const [contexts, setContexts] = useState<{ id: number; name: string }[]>([]);
	const [activeId, setActiveId] = useState<number | null>(user?.activeCompanyId ?? null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		setActiveId(user?.activeCompanyId ?? null);
	}, [user?.activeCompanyId]);

	useEffect(() => {
		if (!user?.id || !['company', 'gardener', 'admin'].includes(user.role)) {
			setContexts([]);
			return;
		}
		getMyCompanyContexts(user.id)
			.then((res) => {
				setContexts(Array.isArray(res.companies) ? res.companies : []);
				const next = res.active_company_id ?? user.activeCompanyId ?? null;
				setActiveId(next != null ? Number(next) : null);
			})
			.catch(() => setContexts([]));
	}, [user?.id, user?.role, user?.activeCompanyId, (user?.companyIds ?? []).join(',')]);

	if (!user || !['company', 'gardener', 'admin'].includes(user.role) || contexts.length <= 1) {
		return null;
	}

	const scopeAll = getStoredCompanyScopeMode() === 'all_companies';

	return (
		<div
			className="company-workspace-strip"
			title={STRIP_TITLE_MULTI}
		>
			<span className="small text-muted text-nowrap mb-0">
				<i className="bi bi-building me-1" aria-hidden />
				Workspace
			</span>
			<select
				className="form-select form-select-sm"
				disabled={busy}
				aria-label="Active company for this page"
				value={scopeAll ? 'all' : String(activeId ?? '')}
				onChange={async (e) => {
					const value = e.target.value;
					if (value === 'all') {
						setBusy(true);
						setStoredCompanyScopeMode('all_companies');
						window.location.reload();
						return;
					}
					const nextCompanyId = Number(value);
					if (!user?.id || !Number.isFinite(nextCompanyId) || nextCompanyId <= 0) return;
					setBusy(true);
					try {
						await setMyActiveCompany(user.id, nextCompanyId);
						await refreshUser(user.id);
						const res = await getMyCompanyContexts(user.id);
						setContexts(Array.isArray(res.companies) ? res.companies : []);
						setActiveId(res.active_company_id != null ? Number(res.active_company_id) : nextCompanyId);
						setStoredCompanyScopeMode('active');
						notifyCompanyWorkspaceChanged();
					} catch (err) {
						console.error(err);
						alert(
							err instanceof Error
								? err.message
								: 'Could not switch company. Try again or use the menu in the top bar.'
						);
					} finally {
						setBusy(false);
					}
				}}
			>
				<option value="all">All companies</option>
				{contexts.map((c) => (
					<option key={c.id} value={String(c.id)}>
						{c.name}
					</option>
				))}
			</select>
		</div>
	);
}
